import { supabase } from '@/lib/supabase';
import { logger } from './logger';

/** Keep the PostgREST `id=in.(...)` filter (and therefore the request URL) small. */
const CHUNK_SIZE = 100;

export interface FetchProfilesResult<T> {
  /** Rows keyed by profile id. Empty when the read failed. */
  profilesById: Record<string, T>;
  profiles: T[];
  /**
   * True when at least one chunk errored — the caller is missing profile data it asked for.
   * Callers must not cache a failed result and should tell the user instead of silently
   * rendering placeholder names.
   */
  failed: boolean;
}

/**
 * Read `profiles` rows by id.
 *
 * Every screen that shows volunteer names does this, and every one of them used to drop the
 * PostgREST error on the floor — an RLS gap or a statement timeout then rendered as a list of
 * people all called "Volunteer". This logs the real error (logger.error reaches production
 * builds, so it is readable over remote inspect on a device) and reports the failure back.
 */
export async function fetchProfilesByIds<T extends { id: string }>(
  ids: (string | null | undefined)[],
  columns: string,
  context = 'fetchProfilesByIds',
): Promise<FetchProfilesResult<T>> {
  const uniqueIds = Array.from(new Set(ids.filter((id): id is string => Boolean(id))));
  if (uniqueIds.length === 0) {
    return { profilesById: {}, profiles: [], failed: false };
  }

  const profiles: T[] = [];
  let failed = false;

  for (let i = 0; i < uniqueIds.length; i += CHUNK_SIZE) {
    const chunk = uniqueIds.slice(i, i + CHUNK_SIZE);
    const { data, error } = await supabase.from('profiles').select(columns).in('id', chunk);

    if (error) {
      failed = true;
      logger.error(`${context}: profiles read failed`, {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint,
        requestedIds: chunk.length,
      });
      continue;
    }

    if (data) profiles.push(...(data as unknown as T[]));
  }

  const profilesById = profiles.reduce(
    (acc, profile) => {
      acc[profile.id] = profile;
      return acc;
    },
    {} as Record<string, T>,
  );

  if (!failed && profiles.length < uniqueIds.length) {
    // No error, just fewer rows than asked for: RLS filtered them out silently.
    logger.warn(
      `${context}: ${uniqueIds.length - profiles.length}/${uniqueIds.length} profiles not readable (RLS)`,
    );
  }

  return { profilesById, profiles, failed };
}
